import merge from 'lodash/merge';
import setPath from 'lodash/set';

import { createStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { dequal } from 'dequal';

import { getCurrentSlugParam, getParentSlugParam, sanitiseSlug } from '../utils/UrlFormatters';
import { omitKeys, sanitiseContent } from './lib';
import { clearDexieStore, storeToDexie, createDexieHistoryEntry, getDexieHistoryEntry } from './dexie';

const sanitiseMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  return Object.entries(metadata).map(([key, value]) => ({ id: crypto.randomUUID(), key, value }));
};

const sanitiseSEO = (seo) => {
  if (!seo || typeof seo !== 'object') {
    return BLANK_SEO;
  }

  return merge({}, BLANK_SEO, seo);
};

const sanitiseAssignedHreflangGroup = (assignedHreflangGroup) => {
  if (!assignedHreflangGroup || typeof assignedHreflangGroup !== 'object') {
    return BLANK_ASSIGNED_HREFLANG_GROUP;
  }

  return assignedHreflangGroup;
};

const createDynamicEntityStoreBase = (initialState) =>
  immer((set, get) => ({
    ...initialState,

    update: (updates) =>
      set((state) => {
        if (typeof updates === 'object' && !Array.isArray(updates)) {
          /**
           * Updates the state with the new values provided in the updates object.
           * This allows for partial updates to the state without replacing the entire object.
           * @param {Object} updates - An object containing key-value pairs to update in the state.
           * @example
           * update({ 'isLoading': true, 'data.seo.title': 'New Title' });
           */
          for (const [path, value] of Object.entries(updates)) {
            setPath(state, path, value);
          }
        } else {
          throw new Error('update must receive only ONE(1) parameter which must be an object with key-value pairs');
        }
      }),

    updateData: (updates) =>
      set((state) => {
        if (typeof updates === 'object' && !Array.isArray(updates)) {
          /**
           * Updates the data object with the new values provided in the updates object.
           * This allows for partial updates to the data without replacing the entire object.
           *
           * @param {Object} updates - An object containing key-value pairs to update in the data.
           * @example
           * updateData({ 'data.content': newContent, 'data.seo.title': 'New Title' });
           */
          for (const [path, value] of Object.entries(updates)) {
            setPath(state.data, path, value);
          }

          createDexieHistoryEntry(state);
        } else {
          throw new Error('updateData must receive only ONE(1) parameter which must be an object with key-value pairs');
        }
      }),

    /**
     * Undo/Redo
     */
    undo: async () => {
      const { currentDataStateIndex } = get();
      if (currentDataStateIndex <= 0) return;

      try {
        const prevState = await getDexieHistoryEntry(currentDataStateIndex - 1);
        if (prevState) {
          set({
            data: prevState.data,
            currentDataStateIndex: currentDataStateIndex - 1,
          });
        }
      } catch (error) {
        console.error('[SFPB - UNDO] Error undoing state:', error);
      }
    },
    redo: async () => {
      const { currentDataStateIndex, totalDataStateIndex } = get();
      if (currentDataStateIndex >= totalDataStateIndex) return;

      try {
        const nextState = await getDexieHistoryEntry(currentDataStateIndex + 1);
        if (nextState) {
          set({
            data: nextState.data,
            currentDataStateIndex: currentDataStateIndex + 1,
          });
        }
      } catch (error) {
        console.error('[SFPB - REDO] Error redoing state:', error);
      }
    },

    /**
     * SEO
     */
    updateSeo: (updates) =>
      set((state) => {
        if (typeof updates === 'object' && !Array.isArray(updates)) {
          /**
           * Updates the data object with the new values provided in the updates object.
           * This allows for partial updates to the data without replacing the entire object.
           *
           * @param {Object} updates - An object containing key-value pairs to update in the data.
           * @example
           * updateData({ 'title': newTitle, 'open_graph.image': 'New Title' });
           */

          for (const [path, value] of Object.entries(updates)) {
            setPath(state.data.seo, path, value);
          }

          createDexieHistoryEntry(state);
        } else {
          throw new Error('updateSeo must receive only ONE(1) parameter which must be an object with key-value pairs');
        }
      }),

    /**
     * Metadata
     */
    addMetadata: (metadata, index) =>
      set((state) => {
        if (index === undefined) {
          state.data.metadata.push(metadata);
          return;
        }

        if (index < 0 || index > state.data.metadata.length) {
          throw new Error('Index out of bounds for adding metadata');
        }

        state.data.metadata.splice(index, 0, metadata);

        createDexieHistoryEntry(state);
      }),
    updateMetadata: (id, metadata) =>
      set((state) => {
        const item = state.data.metadata.find((m) => m.id === id);

        const { key, value } = metadata;

        if (item) {
          item.key = key;
          item.value = value;
        }

        createDexieHistoryEntry(state);
      }),
    removeMetadata: (id) =>
      set((state) => {
        state.data.metadata = state.data.metadata.filter((m) => m.id !== id);
      }),

    /**
     * Content
     */
    addContent: (content, index) =>
      set((state) => {
        if (index === undefined) {
          state.data.content.push(content);
          return;
        }

        if (index < 0 || index > state.data.content.length) {
          throw new Error('Index out of bounds for adding content');
        }

        state.data.content.splice(index, 0, content);

        createDexieHistoryEntry(state);
      }),
    removeContent: (id) =>
      set((state) => {
        state.data.content = state.data.content.filter((c) => c.id !== id);

        createDexieHistoryEntry(state);
      }),
    updateIndividualContent: (id, path, value) =>
      set((state) => {
        const componentIndex = state.data.content.findIndex((c) => c.id === id);
        if (componentIndex === -1) {
          console.error(`[SourceFlow] Component with ID "${id}" not found.`);
          return;
        }

        const component = state.data.content[componentIndex];
        const resolvedPath = [];
        let currentLevel = component;

        // Traverse the object using the provided path, resolving IDs to indices along the way.
        for (const segment of path) {
          if (Array.isArray(currentLevel)) {
            // If the current level is an array, the segment is an ID. Find the index.
            const itemIndex = currentLevel.findIndex((item) => item && item.id === segment);

            if (itemIndex !== -1) {
              resolvedPath.push(itemIndex);
              currentLevel = currentLevel[itemIndex];
            } else {
              // This is a critical error: the path is invalid.
              console.error(`[SourceFlow] Could not resolve ID "${segment}" in path:`, path);
              return; // Abort the update.
            }
          } else {
            // If it's not an array, the segment is a key.
            resolvedPath.push(segment);
            if (currentLevel && typeof currentLevel === 'object') {
              currentLevel = currentLevel[segment];
            }
          }
        }

        // Use the newly resolved path (with indices) to update the state.
        setPath(state.data.content[componentIndex], resolvedPath, value);
        createDexieHistoryEntry(state);
      }),

    /**
     * Dynamic Page Modules
     */
    addDynamicPageModule: (moduleId, propField) =>
      set((state) => {
        const newModule = { dynamic_page_module_id: moduleId, prop_field_name: propField };
        const existingIndex = state.data.modules_connected.findIndex((mod) => mod.prop_field_name === propField);
        if (existingIndex !== -1) {
          state.data.modules_connected[existingIndex] = newModule;
        } else {
          state.data.modules_connected.push(newModule);
        }

        createDexieHistoryEntry(state);
      }),
    removeDynamicPageModule: (propField) =>
      set((state) => {
        state.data.modules_connected = state.data.modules_connected.filter((mod) => mod.prop_field_name !== propField);
        createDexieHistoryEntry(state);
      }),
  }));

const BLANK_SEO = {
  title: '',
  description: '',
  keywords: '',
  canonical: '',
  robots: '',
  open_graph: { image: null },
  alternate: { url: '', hreflang: '' },
  json_schema: '',
};

const BLANK_ASSIGNED_HREFLANG_GROUP = {
  hreflang_group_id: null,
  hreflang_group_key: null,
  id: null,
  locale: null,
};

const BLANK_DATA = {
  metadata: [],
  seo: BLANK_SEO,
  content: [],
  url_slug: '',
  modules_connected: [],
  assigned_category: null,
  assigned_hreflang_group: BLANK_ASSIGNED_HREFLANG_GROUP,
};

const INITIAL_STATE = {
  // UI states
  activePane: 1,
  activeTab: 0,
  hasUnsavedChanges: false,
  hasAssignedCategoryChanged: false,

  // Editor
  viewport: 0, // 0 = Full, 1 = Desktop, 2 = Tablet, 3 = Mobile

  // Undo/Redo states
  currentDataStateIndex: 0,
  totalDataStateIndex: 0,

  // Life Cycle states
  isLoading: false,
  isError: false,
  errorKeys: [], // Errors are stored here as keys which reference the ERRORS array from lib.js

  // Content states
  addContentAtIndex: undefined,
  componentBeingEditedIndex: undefined,
  elementBeingEditedName: undefined,

  // Metadata states
  metadataBeingEditedIndex: undefined,

  // SEO states
  seoBeingEdited: undefined,

  // Misc.
  parentSlug: null,
};

const getInitialDynamicPageOrTemplateData = (dynamicEntity) => {
  if (dynamicEntity) {
    return {
      ...BLANK_DATA,
      ...dynamicEntity,
      seo: sanitiseSEO(dynamicEntity.seo),
      content: sanitiseContent(dynamicEntity.content),
      assigned_hreflang_group: sanitiseAssignedHreflangGroup(dynamicEntity.assigned_hreflang_group),
      metadata: sanitiseMetadata(dynamicEntity.metadata),
    };
  }

  const currentSlug = sanitiseSlug(getCurrentSlugParam());
  return {
    ...BLANK_DATA,
    url_slug: currentSlug || '',
  };
};

const createDynamicPageModuleStore = (dynamicPageModule) => {
  const data = {
    title: dynamicPageModule?.title || '',
    content: dynamicPageModule?.content || [],
  };

  return createStore(subscribeWithSelector(createDynamicEntityStoreBase({ ...INITIAL_STATE, data, initialData: data, activePane: 0 }))); // Set starting pane to be 0 as it's the settings for modules
};

const createDynamicPageOrTemplateStore = (dynamicPageOrTemplate) => {
  const data = getInitialDynamicPageOrTemplateData(dynamicPageOrTemplate);
  const parentSlug = sanitiseSlug(getParentSlugParam());

  return createStore(subscribeWithSelector(createDynamicEntityStoreBase({ ...INITIAL_STATE, parentSlug, data, initialData: data })));
};

export const createDynamicEntityStore = (dynamicEntity, isModule) => {
  let store;

  if (isModule) {
    store = createDynamicPageModuleStore(dynamicEntity);
  } else {
    store = createDynamicPageOrTemplateStore(dynamicEntity);
  }

  // Clear Dexie store on creation
  clearDexieStore();

  // Immediately store the initial data into Dexie as dataStateIndex0
  const initialData = store.getState().data;
  storeToDexie(0, initialData);

  // Subscribe to data changes to automatically update hasUnsavedChanges
  store.subscribe(
    (state) => state.data,
    (currentData) => {
      const { initialData, hasUnsavedChanges, update } = store.getState();

      // Perform the comparison logic, ignoring specific keys
      const stateData = omitKeys(currentData, ['assigned_category', 'assigned_hreflang_group']);
      const initialStateData = omitKeys(initialData, ['assigned_category', 'assigned_hreflang_group']);
      const newHasUnsavedChanges = !dequal(stateData, initialStateData);

      // Only update the state if the value has actually changed
      if (newHasUnsavedChanges !== hasUnsavedChanges) {
        update({ hasUnsavedChanges: newHasUnsavedChanges });
      }
    },
    { equalityFn: dequal },
  );

  // Subscribe to category_id or assigned_category.id changes to update hasAssignedCategoryChanged
  store.subscribe(
    (state) => ({
      category_id: state.data.category_id,
      assigned_category_id: state.data.assigned_category?.id,
    }),
    ({ category_id, assigned_category_id }) => {
      const { update } = store.getState();

      const categoryId = category_id || null;
      const assignedCategoryId = assigned_category_id || null;

      const hasAssignedCategoryChanged = categoryId !== assignedCategoryId;

      update({ hasAssignedCategoryChanged });
    },
  );

  return store;
};
